import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";

import { ErrorBoundary } from "@swa-ui/error";
import { map, merge } from "@swa-ui/object";
import { classNames } from "@swa-ui/string";
import { interpolate } from "@swa-util/string";

import { PlacementsContext } from "../PlacementsContext";
import { PlacementsVisibilityContext } from "../PlacementsVisibilityProvider";
import { usePlacement } from "../usePlacement";
import { ErrorPlacement } from "./ErrorPlacement";
import styles from "./PlacementFactory.module.scss";

/**
 * PlacementFactory renders the placement using the placement ID.
 */

export const PlacementFactory = (props) => {
  const {
    additionalProps,
    alwaysVisible = false,
    className,
    content,
    componentRenderers,
    id,
    options,
    shouldWait,
  } = props;
  const { isComplete, isPlacementLoading, placement, placementMetadata } = usePlacement(id, {
    ...options,
    additionalProps,
  });
  const { placementComponents, placementsConfig = {} } = useContext(PlacementsContext);
  const { registerPlacement } = useContext(PlacementsVisibilityContext) ?? {};
  const placementContent = useMemo(() => placement ?? content ?? {}, [content, placement]);
  const PlacementComponent = useMemo(
    () => placementComponents[content?.displayType ?? placement?.displayType],
    [content?.displayType, placement?.displayType, placementComponents]
  );
  const renderedPlacement = useMemo(() => {
    let memoizedPlacement = null;

    if (id) {
      if (shouldWait) {
        memoizedPlacement =
          isComplete && PlacementComponent && placement ? renderPlacement() : null;
      } else {
        memoizedPlacement =
          (!isPlacementLoading && alwaysVisible) || (PlacementComponent && placement)
            ? renderPlacement()
            : null;
      }
    } else if (content) {
      memoizedPlacement = renderPlacement();
    }

    return memoizedPlacement;
  }, [
    additionalProps,
    alwaysVisible,
    content,
    id,
    isComplete,
    isPlacementLoading,
    placement,
    PlacementComponent,
    shouldWait,
  ]);

  return renderedPlacement;

  function renderPlacement() {
    return (
      <ErrorBoundary FallbackComponent={ErrorPlacement}>
        <div className={getClass()} id={id} ref={registerPlacementElement}>
          <PlacementComponent {...getPlacementProps()} />
        </div>
      </ErrorBoundary>
    );
  }

  function interpolateReactElements(template, data) {
    const interpolationToken = /\$\{([^}]+)\}/g;
    const interpolatedNodes = [];
    let lastIndex = 0;

    template.replace(interpolationToken, (token, key, index) => {
      if (index > lastIndex) {
        interpolatedNodes.push(template.slice(lastIndex, index));
      }

      interpolatedNodes.push(data[key]);
      lastIndex = index + token.length;

      return token;
    });

    if (lastIndex < template.length) {
      interpolatedNodes.push(template.slice(lastIndex));
    }

    return interpolatedNodes;
  }

  function getPlacementProps() {
    const placementDataCollection = {
      ...placementContent,
      ...placementsConfig,
      additionalProps,
      componentRenderers,
    };
    const { templateData: factoryTemplateData = {} } = additionalProps ?? {};
    const { templateData: configTemplateData = {} } = placementsConfig ?? {};
    const templateData = merge(configTemplateData, factoryTemplateData);
    const { templateKeys } = {
      ...placementDataCollection,
      ...(placementDataCollection.placement || {}),
    };
    const interpolationData = templateKeys?.reduce(
      (accumulatedData, key) => ({ ...accumulatedData, [key]: templateData[key] || "" }),
      {}
    );
    let placementProps = placementDataCollection;

    if (templateData && templateKeys?.length > 0) {
      placementProps = map(placementProps, getInterpolatedPlacementValues);
    }

    return placementProps;

    function getInterpolatedPlacementValues(placementValue) {
      let newPlacementValue = placementValue;

      if (!React.isValidElement(placementValue)) {
        if (typeof newPlacementValue === "object") {
          if (Array.isArray(newPlacementValue)) {
            newPlacementValue = newPlacementValue.map(getInterpolatedPlacementValues);
          } else {
            newPlacementValue = map(newPlacementValue, getInterpolatedPlacementValues);
          }
        } else if (typeof placementValue === "string") {
          const hasReactElements = templateKeys.some(
            (key) =>
              placementValue?.includes(`\${${key}}`) && React.isValidElement(interpolationData[key])
          );

          newPlacementValue = hasReactElements
            ? interpolateReactElements(placementValue, interpolationData)
            : interpolate(placementValue, interpolationData);
        }
      }

      return newPlacementValue;
    }
  }

  function getClass() {
    return classNames(
      { [styles.scaleFont]: placementContent.placementData?.scaleFont },
      className,
      styles.placementContainer,
      `placement_${id}`,
      `placementInstance_${placementMetadata?.id}`
    );
  }

  function registerPlacementElement(element) {
    if (element && id) {
      registerPlacement?.(element, { id, placementContent, placementMetadata });
    }
  }
};

PlacementFactory.displayName = "PlacementFactory";
PlacementFactory.propTypes = {
  /**
   * Set to true if the placement should always be visible, regardless of error.
   */
  alwaysVisible: PropTypes.bool,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Inline placement content for direct implementation embedding of nested placements. Must either provide this or placement id. */
  content: PropTypes.object,

  /**
   * Placement ID to use to identify content to render. Must either provide this prop or inline content.
   */
  id: PropTypes.string,

  /**
   * Object to pass options to usePlacement.
   */
  options: PropTypes.object,

  /**
   * Boolean to control if component should wait until placement call is done.
   */
  shouldWait: PropTypes.bool,
};

PlacementFactory.defaultProps = {
  shouldWait: true,
};
